import { RecommendationContext } from '@/configs/recommendations';
import Service from '../../Service';

import { IPage } from '../../models/Page';
import { siteCached } from '../../utils/siteCached/siteCached';
import { Config } from '../ConfigurationService';
import ConfigurationService from '../ConfigurationService/ConfigurationService';
import { PersonalizationService } from '../PersonalizationService';
import { ProductListService } from '../ProductListService';
import { IRecommendationContextConfig } from './IRecommendationContextConfig';
import IRecommendationsData from './IRecommendationsData';
import { IStreamedRecommendationsData } from './IStreamedRecommendationsData';
import RecommendationServiceMock from './RecommendationServiceMock';
import { getContextConfig } from './getContextConfig';

/**
 * RecommendationService handle the retrieval of recommendation products from the DY.
 * Integration Service.
 */
export class RecommendationService extends Service {
  /**
   * The Recommendations config.
   * @returns A `Config<'recommendations'>`.
   */
  @siteCached
  private get config(): Config<'recommendations'> {
    return ConfigurationService.getConfig('recommendations');
  }

  /**
   * The Dynamic Yield config.
   * @returns A `Config<'dynamicYield'>`.
   */
  @siteCached
  private get dyConfig(): Config<'dynamicYield'> {
    return ConfigurationService.getConfig('dynamicYield');
  }

  /** Initializes the recommendation service. */
  public constructor() {
    super();
  }

  /**
   * Gets the recommendation config values for a given context.
   * @param context - The recommendation context.
   * @returns - An object holding values useful to the frontend.
   * @throws If an invalid context is supplied.
   */
  public getContextConfig(
    context: RecommendationContext
  ): IRecommendationContextConfig {
    const { isRecommendationEnabled, isSlideOnMobile } =
      getContextConfig(context);
    return { isRecommendationEnabled, isSlideOnMobile };
  }

  /**
   * `getRecommendationsForCurrentUser` stablish the communication with DYService to
   * retrieve recommendation products based in a context: HOMEPAGE, PDP, CART, ACCOUNT.
   * @param context - The context we want to retrieve the recommendation products.
   * @param page - The current page the user is on.
   * @param contextData - Optional page-type-specific data for the DY context
   * (e.g., the product SKU for PRODUCT pages).
   * @returns A list of product SKUs with a title.
   */
  public async getRecommendations(
    context: RecommendationContext,
    page: IPage,
    contextData?: ReadonlyArray<string>
  ): Promise<IRecommendationsData> {
    const isDYEnabled = this.dyConfig.getSetting('isDYEnabled').value;

    const fallbackRecommendations = {
      title: '',
      productList: [],
      parentTitle: '',
      slotIds: []
    };

    // We currently have no default way of getting recommendations.
    // So if DY is not enabled then we return an empty list.
    if (!isDYEnabled) {
      return fallbackRecommendations;
    }

    /**
     * Gets the direct recomendation experience and pulls a list of products off of it.
     * In the future we will use the dynamic recommenations.
     */
    const data = await PersonalizationService.getRecommendationsForCurrentUser(
      context,
      page,
      contextData
    );

    return data;
  }

  /**
   * Fetches recommendations and streams product data for a given context.
   * This method abstracts the logic of checking if recommendations are enabled,
   * fetching recommendations, and streaming product data.
   * @param context - The recommendation context.
   * @param page - The current page the user is on.
   * @param contextData - Optional page-type-specific data for the DY context
   * (e.g., the product SKU for PRODUCT pages).
   * @returns Streamed recommendations data with grid items.
   */
  public async streamProductsRecommendations(
    context: RecommendationContext,
    page: IPage,
    contextData?: ReadonlyArray<string>
  ): Promise<IStreamedRecommendationsData> {
    // Check if the context is enabled before fetching recommendations
    const { isRecommendationEnabled } = this.getContextConfig(context);

    if (!isRecommendationEnabled) {
      return {
        title: null,
        parentTitle: null,
        gridItems: [],
        isRecommendationEnabled: false
      };
    }

    const { productList, title, parentTitle } = await this.getRecommendations(
      context,
      page,
      contextData
    );

    if (productList.length === 0) {
      return {
        title,
        parentTitle,
        gridItems: [],
        isRecommendationEnabled: true
      };
    }

    // Get streaming product data
    const gridItems =
      await ProductListService.streamProductsFromSkus(productList);

    return { title, parentTitle, gridItems, isRecommendationEnabled: true };
  }
}

export default RecommendationService.withMock(
  new RecommendationServiceMock(RecommendationService)
) as unknown as RecommendationService;
